import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MenuIcon } from "../../constants/menuIcons";
import { useTranslation } from "../../contexts/TranslationContext";
import i18n from "../../i18n";
import { getStaticTranslation } from "../../translations/registerStaticTranslations";

// Full-width mega-menu panel under the header (DoD 22 — Marin's design, rebuilt
// in the app idiom: Tailwind utilities, platform blue #2283FF, existing image
// icons). Content is fully data-driven: `categories` and `pills` come from
// useHeaderNavigation, so the per-role / per-perspective rules (incl. E-20c)
// carry over. Layout adapts to the item count — no hard-coded A/B variant.
//
// The desktop panel renders 4 category columns (2 at the medium desktop band),
// an optional "Quick access" pill row, and an optional footer. A category with
// more than MAX_VISIBLE entries collapses the overflow behind a "Show more".

const MAX_VISIBLE = 5;

const CategoryColumn = ({ cat, isActive, tr }) => {
  const [expanded, setExpanded] = useState(false);
  const hasMore = cat.items.length > MAX_VISIBLE;
  const visible = expanded || !hasMore ? cat.items : cat.items.slice(0, MAX_VISIBLE);
  const moreLabel = expanded
    ? tr("Show less")
    : tr("Show more (+{count})").replace("{count}", cat.items.length - MAX_VISIBLE);
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 px-2.5 pb-2.5 border-b border-[#EFF1F4] mb-2">
        {tr(cat.label)}
      </div>
      <div className="flex flex-col gap-0.5">
        {visible.map((item) => {
          const active = isActive?.(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`group flex gap-3 p-2.5 rounded-xl hover:bg-[#F3F6FB] ${
                active ? "bg-[#EEF4FE]" : ""
              }`}
            >
              <MenuIcon
                name={item.iconKey}
                size={22}
                className="flex-shrink-0 mt-0.5 text-[#374151]"
              />
              <div className="min-w-0">
                <div
                  className={`flex items-center gap-2 text-sm font-semibold leading-snug group-hover:text-[#2283FF] ${
                    active ? "text-[#2283FF]" : "text-[#0F1419]"
                  }`}
                >
                  {tr(item.title)}
                  {/* Design 4a: new-recommendation count on Scout Requests. */}
                  {item.badge > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#2283FF] text-white text-[11px] font-bold leading-none">
                      {item.badge}
                    </span>
                  )}
                  {/* Design 7a: NEW launch badge (e.g. Last Minute). */}
                  {item.isNew && (
                    <span className="inline-flex items-center rounded-full bg-[#E8F1FF] text-[#1B6FE0] text-[10px] font-bold leading-none px-1.5 py-0.5 uppercase tracking-[0.04em]">
                      New
                    </span>
                  )}
                  {/* What's New unread indicator (D-003: a plain dot). */}
                  {item.dot && (
                    <span
                      className="w-2 h-2 rounded-full bg-[#2283FF] inline-block"
                      aria-label="New updates"
                    />
                  )}
                </div>
                {item.description && (
                  <div className="text-[12.5px] text-gray-500 leading-snug mt-0.5">
                    {tr(item.description)}
                  </div>
                )}
              </div>
            </Link>
          );
        })}
        {hasMore && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-left px-2.5 py-2 text-[13px] font-semibold text-[#2283FF] rounded-xl hover:bg-[#F3F6FB]"
          >
            {moreLabel}
          </button>
        )}
      </div>
    </div>
  );
};

const MegaMenu = ({
  isOpen,
  onClose,
  anchorTop = 0,
  categories = [],
  pills = [],
  navItems = [],
  showQuickAccess = true,
  // Footer renders only entries with real routes (verified in routes/index.js):
  // Settings -> /profile (the platform's settings equivalent), FAQ -> /faq.
  // "Help Center" from the design is dropped — no target, and the global
  // support FAB already covers help.
  showFooter = true,
  isActive,
}) => {
  // The panel content is inserted on open, after the page's language scan has
  // already run, so nudge the runtime translator to translate the fresh nodes
  // (covers the data-driven item titles/descriptions). The menu's own
  // structural labels are translated synchronously at render via `tr` so they
  // are correct immediately regardless of observer timing.
  const { currentLanguage, translateNewContent } = useTranslation();
  const tr = (s) => getStaticTranslation(i18n, s, currentLanguage) || s;
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = setTimeout(() => translateNewContent && translateNewContent(), 120);
    return () => clearTimeout(id);
  }, [isOpen, translateNewContent]);

  // T050-4: Escape closes the menu. Click-outside already worked (the dim
  // overlay below and the header's transparent `fixed inset-0 z-40` backdrop
  // both call onClose on click), but no key listener existed anywhere in the
  // header, so Esc did nothing. Same document-level keydown pattern the
  // sibling header dropdowns use (components/header/NotificationDropdown.js:77
  // and ProfileDropdown.js:100).
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Dim overlay anchored to the live header bottom; clicking it closes. */}
      <div
        className="fixed left-0 right-0 bottom-0 bg-black/30 z-40"
        style={{ top: anchorTop }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="relative z-50 bg-white border-b border-gray-200 shadow-[0_32px_56px_-28px_rgba(15,20,25,0.25)] overflow-auto"
        style={{ maxHeight: `calc(100vh - ${anchorTop}px)` }}
      >
        {/* Hotfix D30: the 1260px cap wrapped the 5 columns onto 2 rows on large
            desktops (5×minmax(220) + gaps + px-8 needs >1300px). Raised to 1800px
            so all five sit in one row on 1920/2560, centered (no empty decay at
            2560); 1280/1440 are viewport-limited below the cap and unchanged. */}
        <div className="max-w-[1800px] mx-auto px-8 pt-6 pb-5">
          {showQuickAccess && pills.length > 0 && (
            <div className="flex items-center flex-wrap gap-2.5 pb-4 mb-4 border-b border-[#EFF1F4]">
              <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mr-1">
                {tr("Quick access")}
              </span>
              {pills.map((pill) => {
                return (
                  <Link
                    key={pill.id || pill.path}
                    to={pill.path}
                    onClick={onClose}
                    className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-[13px] font-semibold hover:bg-[#F3F6FB] ${
                      pill.badge > 0 || pill.newPill
                        ? "border-[#BFDBFE] bg-[#E8F1FF] text-[#1B6FE0]"
                        : "border-gray-200 bg-white text-[#0F1419]"
                    }`}
                  >
                    {pill.iconKey && (
                      <MenuIcon name={pill.iconKey} size={15} strokeWidth={2} className="flex-shrink-0" />
                    )}
                    <span>{tr(pill.label)}</span>
                    {pill.badge > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#2283FF] text-white text-[11px] font-bold leading-none">
                        {pill.badge}
                      </span>
                    )}
                    {pill.newPill && (
                      <span className="rounded-full bg-[#2283FF] text-white text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 leading-none">
                        New
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          )}

          {/* Primary nav — only in the panel below xl, where the inline nav
              is hidden (mirrors the design's tablet behaviour). Above xl these
              live inline in the top bar. */}
          {navItems.length > 0 && (
            <div className="xl:hidden flex flex-wrap gap-1 pb-4 mb-4 border-b border-[#EFF1F4]">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={`px-3 py-2 rounded-[10px] text-sm font-medium ${
                    isActive?.(item.path)
                      ? "text-[#2283FF] bg-[#EEF4FE]"
                      : "text-[#4B5563] hover:bg-[#F3F6FB] hover:text-[#0F1419]"
                  }`}
                >
                  {tr(item.label)}
                </Link>
              ))}
            </div>
          )}

          <div
            className="grid gap-x-9 gap-y-2"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}
            onClick={(e) => {
              // Navigating via a category link should also close the panel.
              if (e.target.closest("a")) onClose?.();
            }}
          >
            {categories.map((cat) => (
              <CategoryColumn key={cat.id} cat={cat} isActive={isActive} tr={tr} />
            ))}
          </div>

          {showFooter && (
            <div className="flex gap-6 mt-6 pt-4 border-t border-[#EFF1F4]">
              <Link to="/profile" onClick={onClose} className="text-[13px] text-gray-500 hover:text-[#0F1419]">
                {tr("Settings")}
              </Link>
              <Link to="/faq" onClick={onClose} className="text-[13px] text-gray-500 hover:text-[#0F1419]">
                {tr("FAQ")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MegaMenu;
